import axios, { AxiosRequestConfig } from "axios";
import { getLocalValue, removeLocalValue } from "@/hooks/useLocalStorage";
import { AuthInterfaces } from "@/interfaces/auth";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipErrorHandling?: boolean;
  // Marks the request as already retried so the 401 interceptor rejects instead of refreshing the token
  _retry?: boolean;
}

const baseURL = process.env.VUE_APP_API_URL;

class AuthService {
  static login(model = {}) {
    const url = `${baseURL}/account/donor/register`;

    return axios.post(url, model);
  }

  static sendSigninOTP(mobile_number = 0) {
    const url = `${baseURL}/account/donor/register`;

    return axios.post(url, { mobile_number });
  }

  static donerSignup(mobile_number = 0) {
    const url = `${baseURL}/account/donor/register`;

    return axios.post(url, { mobile_number });
  }

  static sendSignupOTP(mobile_number = 0) {
    const url = `${baseURL}/account/donor/register`;

    return axios.post(url, { mobile_number });
  }

  static tokenExchange({ params = {}, config = {}, realm = "" }) {
    const url =
      process.env.VUE_APP_KEYCLOAK_URL +
      `/realms/${realm}/protocol/openid-connect/token`;

    return axios.post(url, params, config);
  }

  static getRefreshToken({ params = {}, config = {}, realm = "" }) {
    const url =
      process.env.VUE_APP_KEYCLOAK_URL +
      `/realms/${realm}/protocol/openid-connect/token`;

    return axios.post(url, params, config);
  }

  static getUserInformation() {
    const url = `${baseURL}/account/user-profile`;

    return axios.get(url);
  }

  static forgetPasswordSendEmail({email, type}: {email: string, type: string}) {
    let url = `${baseURL}/account/password-reset-request/`;
    url += type ? `?type=${type}` : "";

    return axios.post(url, { email });
  }

  static setPassword(
    password: string,
    confirm_password: string,
    token: string
  ) {
    const url = `${baseURL}/account/password-reset/`;

    return axios.patch(url, { password, confirm_password, token });
  }

  static resetPassword({ old_password, password, confirm_password }: any) {
    const url = `${baseURL}/account/set-password/`;

    return axios.patch(url, { old_password, password, confirm_password });
  }

  static async logout() {
    const url = `${baseURL}/account/logout`;

    return axios.get(url).then(() => {
      if (localStorage.getItem("useremail")) {
        localStorage.removeItem("useremail");
      }
      if (getLocalValue("charity_account_state")) {
        removeLocalValue("charity_account_state");
      }
      if (getLocalValue("donor_mobile_number")) {
        removeLocalValue("donor_mobile_number");
      }
    });
  }

  static clearAllUserData() {
    const keysToRemove = [
      "username",
      "useremail", 
      "mobile_number",
      "donor_mobile_number",
      "currentUser",
      "charity_account_state",
      "new_donor",
      "user_charity_id",
      "client-id",
      "charity_name",
      "charity_approval_info",
      "passwordSet"
    ];
    
    keysToRemove.forEach(key => {
      removeLocalValue(key);
    });
  }

  static getLastAgreement() {
    const url = `${baseURL}/charity/request/last-agreement/`;

    return axios.get(url);
  }

  static uploadSignedAgreement(file: any) {
    const url = `${baseURL}/charity/request/upload-singed-agreement/`;

    return axios.post(url, { file });
  }

  static createAndSendOtp(mobile_number: string, customPayload = null) {
    const url = `${baseURL}/account/auth/otp-login/`;

    const payload = customPayload || { mobile_number };
    
    return axios.post(url, payload);
  }

  static customUserLogin(payload: {
    mobile_number: string;
    password: string;
    user_type: string;
  }, skipErrorHandling = false, skipTokenRefresh = false) {
    const url = `${baseURL}/account/auth/login/`;

    const config: CustomAxiosRequestConfig = { skipErrorHandling };
    // Admin wrong password returns 401 with the remaining attempts — must reach the login form, not trigger a refresh
    if (skipTokenRefresh) config._retry = true;

    return axios.post(url, payload, config);
  }

  static verifyOtp(payload: any, skipErrorHandling = false) {
    const url = `${baseURL}/account/auth/otp-verify/`;

    return axios.post(url, payload, { skipErrorHandling } as CustomAxiosRequestConfig);
  }

  static verifyAdminLoginOtp(payload: AuthInterfaces.IAdminLoginOtpVerifyPayload) {
    const url = `${baseURL}/account/auth/admin-login/verify-otp/`;

    return axios.post(url, payload, { skipErrorHandling: true, _retry: true } as CustomAxiosRequestConfig);
  }

  static getCustomRefreshToken(refreshToken: string) {
    const url = `${baseURL}/account/auth/refresh/`;

    return axios.post(url, { refresh: refreshToken });
  }

  static customLogout(refreshToken: string) {
    const url = `${baseURL}/account/auth/logout/`;

    return axios.post(url, { refresh: refreshToken });
  }

  static getCharityLoginFlag()
  {
    const url = `${baseURL}/charity-login-flag/`;

    return axios.get<AuthInterfaces.ICharityLoginFlagResponse>(url);
  }

  static deleteAccount(donorMobile: string) {
    const url = `${baseURL}/account/auth/delete-user/`;

    return axios.post(url, { mobile_number: donorMobile });
  }
}

export default AuthService;
